import { useReducer } from 'react'
import {
  EditorContentfulConfiguration,
  FileURL,
  OptionSelection,
  PreloadConfiguration,
  ProductVariant,
  SelectFn,
  SelectionState,
  SelectorConfiguration,
} from '../../types'
import reducer from './reducer'
import {
  extractDeps,
  getDefaultSelection,
  getProductOptionSelected,
  getProductOptionsWithSalesData,
  getSelectorsConfiguration,
} from './useProductOptionSelection.utils'
import sortBy from 'lodash/sortBy'
import isEmpty from 'lodash/isEmpty'
import partition from 'lodash/partition'

function init({
  productCategorySlug,
  productVariantsSales,
  editorConfiguration,
}: {
  productCategorySlug: string
  productVariantsSales: ProductVariant[]
  editorConfiguration: EditorContentfulConfiguration
}): SelectionState {
  const dependencies = extractDeps(productVariantsSales)
  const values = getDefaultSelection(
    productVariantsSales,
    getSelectorsConfiguration({
      values: [],
      editorConfiguration,
      dependencies,
    })
  )

  return {
    productCategorySlug,
    productVariantsSales: sortBy(productVariantsSales, 'price'),
    editorConfiguration,
    dependencies,
    values,
    filesUrls: [],
    selectedOptionLabel: '',
  }
}

export default function useProductOptionSelection({
  productCategorySlug,
  productVariantsSales,
  editorConfiguration,
}: {
  productCategorySlug: string
  productVariantsSales: ProductVariant[]
  editorConfiguration: EditorContentfulConfiguration
}): {
  state: SelectionState
  selectorsConfiguration: () => SelectorConfiguration[]
  select: SelectFn
  productOptionSelected: () => ProductVariant
  selectedOptionLabel: string
  upload: (fileUrls: FileURL[]) => void
  removeUploadFile: (fileUrl: FileURL) => void
  isDesignReadyForAddToCart: () => boolean
  getFileUploadedUrls: () => string[]
  editingItemId: () => string
  setPreloadConfiguration: (preloadConfiguration: PreloadConfiguration) => void
  clearUploadedFiles: () => void
  resetProductOptionSelection: () => void
  getQuantityOptions: () => {
    quantityOptions: ProductVariant[]
    sampleOptions: ProductVariant[]
    quantityOptionsWithoutSamples: ProductVariant[]
  }
} {
  const [state, dispatch] = useReducer(
    reducer,
    {
      productCategorySlug,
      productVariantsSales: productVariantsSales,
      editorConfiguration,
    },
    init
  )

  function select(option: OptionSelection) {
    dispatch({
      type: 'OPTION_SELECT',
      payload: option,
    })
  }

  function upload(filesUrls: string[]): void {
    dispatch({
      type: 'UPLOAD_FILE',
      payload: filesUrls,
    })
  }

  function removeUploadFile(fileUrl: FileURL): void {
    dispatch({
      type: 'REMOVE_UPLOAD_FILE',
      payload: fileUrl,
    })
  }

  function clearUploadedFiles(): void {
    dispatch({
      type: 'CLEAR_UPLOADED_FILES',
    })
  }

  function resetProductOptionSelection(): void {
    dispatch({
      type: 'RESET_PRODUCT_OPTION',
    })
  }

  const setPreloadConfiguration = (
    preloadConfiguration: PreloadConfiguration
  ) => {
    if (isEmpty(preloadConfiguration)) {
      return
    }

    // Update values and fileUrls
    dispatch({
      type: 'PRELOAD_CONFIGURATION',
      payload: preloadConfiguration,
    })
  }

  // DERIVATIVE DATA
  const selectorsConfiguration = () => {
    return getSelectorsConfiguration(state)
  }

  const productOptionSelected = () => {
    const { selection } = getProductOptionSelected(state)
    return selection
  }

  const getQuantityOptions = () => {
    const possibleQuantityOptions = getProductOptionsWithSalesData(state)
    const [sampleOptions, quantityOptionsWithoutSamples] = partition(
      possibleQuantityOptions,
      (option) => option.variantSku?.includes('SAMPLE')
    )

    return {
      sampleOptions,
      quantityOptionsWithoutSamples,
      quantityOptions: possibleQuantityOptions,
    }
  }

  const isDesignReadyForAddToCart = () => {
    return !isEmpty(state.filesUrls)
  }

  const getFileUploadedUrls = () => {
    return state.filesUrls
  }

  const editingItemId = () => {
    return state.itemId ?? null
  }

  const selectedOptionLabel = state.selectedOptionLabel

  return {
    state,
    selectorsConfiguration,
    select,
    productOptionSelected,
    upload,
    removeUploadFile,
    isDesignReadyForAddToCart,
    getFileUploadedUrls,
    editingItemId,
    setPreloadConfiguration,
    getQuantityOptions,
    clearUploadedFiles,
    resetProductOptionSelection,
    selectedOptionLabel,
  }
}
